import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/leave/accrual-rules")({
  component: AccrualRulesPage,
});

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";

import { useDebounce } from "@/hooks/use-debounce";

import useFetchAccrualRules, {
  type AccrualRuleOrdering,
} from "@/hooks/api/leave/accrual-rules/useFetchAccrualRules";
import useCreateAccrualRule from "@/hooks/api/leave/accrual-rules/useCreateAccrualRule";
import useUpdateAccrualRule from "@/hooks/api/leave/accrual-rules/useUpdateAccrualRule";
import useDeleteAccrualRule from "@/hooks/api/leave/accrual-rules/useDeleteAccrualRule";
import useFetchLeaves from "@/hooks/api/leave/leave/useFetchLeaves";

import { AccrualRulesTable } from "@/components/crud/table/AccrualRulesTable";
import { AccrualRuleFormSheet } from "@/components/crud/forms/AccrualRuleFormSheet";
import { RunAccrualDialog } from "@/components/crud/forms/RunAccrualDialog";

import type {
  LeaveAccrualRule,
  LeaveAccrualRulePayload,
  AccrualGender,
  AccrualEmploymentType,
} from "@/types/leave";

import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function AccrualRulesPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "leave.type.manage");
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        amount: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      AccrualRuleOrdering
    >({
      name: null,
      amount: null,
      created_at: null,
      updated_at: null,
    });

  const [statusFilter, setStatusFilter] = useState<
    "true" | "false" | "clear" | undefined
  >();
  const [leaveFilter, setLeaveFilter] = useState<string | undefined>();
  const [genderFilter, setGenderFilter] = useState<
    AccrualGender | "clear" | undefined
  >();
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState<
    AccrualEmploymentType | "clear" | undefined
  >();
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<LeaveAccrualRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<LeaveAccrualRule | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const accrualRulesQuery = useFetchAccrualRules({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      statusFilter === "clear"
        ? undefined
        : statusFilter === "true"
          ? true
          : statusFilter === "false"
            ? false
            : undefined,
    leave: leaveFilter ? Number(leaveFilter) : undefined,
    gender: genderFilter === "clear" ? undefined : genderFilter,
    employment_type:
      employmentTypeFilter === "clear" ? undefined : employmentTypeFilter,
  });
  const accrualRules = accrualRulesQuery.data?.results ?? [];
  const count = accrualRulesQuery.data?.count ?? 0;

  const createAccrualRule = useCreateAccrualRule();
  const updateAccrualRule = useUpdateAccrualRule();
  const deleteAccrualRule = useDeleteAccrualRule();

  const isMutating =
    createAccrualRule.isPending ||
    updateAccrualRule.isPending ||
    deleteAccrualRule.isPending;
  const isLoading = accrualRulesQuery.isPending || isMutating;
  const isFetching = accrualRulesQuery.isFetching || isMutating;

  const resetFilters = () => {
    setOrdering({
      name: null,
      amount: null,
      created_at: null,
      updated_at: null,
    });
    setStatusFilter(undefined);
    setLeaveFilter(undefined);
    setGenderFilter(undefined);
    setEmploymentTypeFilter(undefined);
    setGlobalFilter("");
    resetDebounce();
    setPage(1);
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    leaveFilter,
    genderFilter,
    employmentTypeFilter,
    setPage,
  ]);

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    setIsSheetOpen(true);
  };

  const handleEdit = (rule: LeaveAccrualRule) => {
    setEditingRule({ ...rule });
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (rule: LeaveAccrualRule) => {
    setRuleToDelete(rule);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;
    try {
      await deleteAccrualRule.mutateAsync({ id: ruleToDelete.id });
      toast.success("Accrual rule deleted.");
      setIsDeleteDialogOpen(false);
      setRuleToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
      setIsDeleteDialogOpen(false);
    }
  };

  const handleFormSubmit = async (data: LeaveAccrualRulePayload) => {
    if (editingRule) {
      await updateAccrualRule.mutateAsync({ id: editingRule.id, payload: data });
      toast.success("Accrual rule updated.");
    } else {
      await createAccrualRule.mutateAsync(data);
      toast.success("Accrual rule created.");
    }
    setIsSheetOpen(false);
  };

  // Accrual rules can only target paid leave types (backend rejects unpaid).
  // Feeds the SelectFilter dropdown only; the form sheet's picker paginates
  // itself. Capped at the backend max, so orgs beyond 100 types can't filter
  // by the overflow -- tracked separately from the form-side fix.
  const leavesQuery = useFetchLeaves({ page_size: 100, is_paid: true });
  const leaves = leavesQuery.data?.results ?? [];

  const leaveOptions = leaves.map((l) => ({
    label: l.name,
    value: String(l.id),
  }));

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Accrual Rules"
          description="Define monthly leave accrual policies for your employees."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Rule"
          isLoading={isFetching}
        >
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsRunDialogOpen(true)}
              disabled={isFetching}
            >
              <Play className="mr-2 h-4 w-4" />
              Run Accrual
            </Button>
          )}
        </PageHeader>

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search by name or leave type..."
            value={globalFilter}
            onChange={setGlobalFilter}
          />

          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
              { label: "All", value: "clear" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />

          <SelectFilter
            placeholder="Leave Type"
            options={[{ label: "All", value: "clear" }, ...leaveOptions]}
            value={leaveFilter}
            onChange={(v) => setLeaveFilter(v === "clear" ? undefined : v)}
          />

          <SelectFilter
            placeholder="Gender"
            options={[
              { label: "All", value: "clear" },
              { label: "Male", value: "Male" },
              { label: "Female", value: "Female" },
              { label: "Other", value: "Other" },
            ]}
            value={genderFilter}
            onChange={setGenderFilter}
          />

          <SelectFilter
            placeholder="Employment Type"
            options={[
              { label: "All", value: "clear" },
              { label: "Full-time", value: "Full-time" },
              { label: "Part-time", value: "Part-time" },
              { label: "Contract", value: "Contract" },
              { label: "Internship", value: "Internship" },
            ]}
            value={employmentTypeFilter}
            onChange={setEmploymentTypeFilter}
          />
        </FiltersPanel>

        <AccrualRulesTable
          accrualRules={accrualRules}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <AccrualRuleFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingRule={editingRule}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Accrual Rule"
          description={`Are you sure you want to delete the rule "${ruleToDelete?.name}"? If this rule has already run, consider deactivating it instead to preserve the audit log.`}
          itemName={ruleToDelete?.name}
          isLoading={isMutating}
          destructive
        />

        <RunAccrualDialog
          isOpen={isRunDialogOpen}
          onClose={() => setIsRunDialogOpen(false)}
          onSuccess={resetFilters}
        />
      </div>
    </SidebarInset>
  );
}
